import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const BatchMixingPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    batch_overview: false,
    batch_ingredients: false,
    batch_costs: false,
    batch_summary: false
  });
  
  // Batch mixing pages mapping
  const batchMixingPages = [
    'batch_overview', 
    'batch_ingredients', 
    'batch_costs', 
    'batch_summary'
  ];
  
  const pageNameMapping = {
    'batch_overview': 'Batch Overview Page',
    'batch_ingredients': 'Batch Ingredients Page',
    'batch_costs': 'Batch Costs Page',
    'batch_summary': 'Batch Summary Page'
  };
  
  const pageIconMapping = {
    'batch_overview': <DashboardIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_ingredients': <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_costs': <AttachMoneyIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_summary': <SummarizeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderBatchMixingPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'batch_overview') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_overview'));
    } else if (pageKey === 'batch_ingredients') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_ingredients'));
    } else if (pageKey === 'batch_costs') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_costs'));
    } else if (pageKey === 'batch_summary') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_summary'));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete'));
    const approvePerm = permissions.find(p => p.name === 'production.batch_summary.approve');

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (editPerm || (pageKey === 'batch_summary' && approvePerm)) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission - for batch_costs, batch_overview, batch_ingredients */}
            {editPerm && (pageKey === 'batch_costs' || pageKey === 'batch_overview' || pageKey === 'batch_ingredients') && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'batch_ingredients' && deletePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission - only for batch_ingredients */}
            {deletePerm && pageKey === 'batch_ingredients' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'batch_summary' && approvePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Approve Permission - only for batch_summary */}
            {approvePerm && pageKey === 'batch_summary' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Approve Batches
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can approve production batches
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[approvePerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[approvePerm.id]) {
                      newPermissions[approvePerm.id] = {};
                    }
                    newPermissions[approvePerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {batchMixingPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderBatchMixingPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default BatchMixingPermissions;
